/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { CSSProperties } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

enum ScrollDirection {
  Up = 'up',
  Down = 'down',
  Unknown = 'unknown',
}

const defaultWrapperStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  minHeight: '100%',
  flexGrow: 1,
};
const defaultStyles: CSSProperties = {
  position: 'sticky',
  top: '0',
};

type FuncRef = (el: HTMLElement | null) => void;

type Version2RenderFuncArgs = {
  elementRef: FuncRef;
  style: CSSProperties;
};

type Version2RenderFunc = (args: Version2RenderFuncArgs) => React.ReactElement<any>;

type AdvancedStickyProps = {
  children: Version2RenderFunc;
  scrollElement: string | HTMLElement;
  topOffset?: number;
  bottomOffset?: number;
  disabled?: boolean;
};

type AdvancedStickyState = {
  style: CSSProperties;
  wrapperStyle: CSSProperties;
};

export class AdvancedSticky extends React.Component<AdvancedStickyProps, AdvancedStickyState> {
  state = {
    style: defaultStyles,
    wrapperStyle: defaultWrapperStyles,
  };

  static defaultProps = {
    scrollElement: 'window',
    bottomOffset: 0,
    topOffset: 0,
  };

  lastDirection: ScrollDirection = ScrollDirection.Unknown;
  lastScrollTop: number = 0;

  wrapperEl: HTMLElement | null = null;
  el: HTMLElement | null = null;

  scrollEl: HTMLElement | null = null;

  disabled: boolean = false;

  wrapperRef = (wrapperEl: HTMLElement | null) => {
    if (this.wrapperEl) {
      this.resizeObserver.unobserve(this.wrapperEl);
    }
    if (this.wrapperEl === wrapperEl) {
      return;
    }
    if (wrapperEl) {
      this.resizeObserver.observe(wrapperEl);
    }
    this.wrapperEl = wrapperEl;
    this.updateScrollEl();
  };

  elementRef = (el: HTMLElement | null) => {
    if (this.el) {
      this.resizeObserver.unobserve(this.el);
    }
    if (this.el === el) {
      return;
    }
    if (el) {
      this.resizeObserver.observe(el);
    }
    this.el = el;
    this.updateScrollEl();
  };

  recalculate = () => {
    const { disabled, el, scrollEl, wrapperEl, lastDirection, lastScrollTop } = this;
    if (!el || !scrollEl || !wrapperEl) {
      console.error('Missing required elements', { el, scrollEl, wrapperEl });
      return;
    }
    if (disabled) {
      this.setState({ style: {}, wrapperStyle: {} });
      return;
    }

    const { topOffset, bottomOffset } = this.props;

    const elReact = el.getBoundingClientRect();
    const wrapperRect = wrapperEl.getBoundingClientRect();
    const scrollTop = scrollEl.scrollTop;

    const scrollDirection = scrollTop > lastScrollTop ? ScrollDirection.Down : ScrollDirection.Up;
    this.lastDirection = scrollDirection;
    this.lastScrollTop = scrollTop;

    if (scrollDirection !== lastDirection) {
      if (elReact.height <= window.innerHeight) {
        this.setState({
          wrapperStyle: { ...defaultWrapperStyles },
          style: { ...defaultStyles, position: 'sticky', top: topOffset },
        });
      } else if (scrollDirection === ScrollDirection.Down) {
        this.setState({
          wrapperStyle: {
            ...defaultWrapperStyles,
            justifyContent: 'flex-end',
            paddingTop: Math.abs(wrapperRect.top - elReact.top),
          },
          style: { ...defaultStyles, top: 'auto', bottom: bottomOffset },
        });
      } else if (scrollDirection === ScrollDirection.Up) {
        this.setState({
          wrapperStyle: {
            ...defaultWrapperStyles,
            paddingBottom: Math.abs(wrapperRect.bottom - elReact.bottom),
          },
          style: { ...defaultStyles, top: topOffset },
        });
      }
    }
  };

  resizeObserver = new ResizeObserver(this.recalculate);

  updateScrollEl = () => {
    if (this.scrollEl) {
      this.resizeObserver.unobserve(this.scrollEl);
      this.scrollEl.removeEventListener('scroll', this.recalculate);
      this.scrollEl = null;
    }

    const { scrollElement } = this.props;

    if (typeof scrollElement === 'string') {
      this.scrollEl =
        scrollElement === 'window' ? document.documentElement : document.querySelector<HTMLElement>(scrollElement);
    } else {
      this.scrollEl = scrollElement;
    }

    if (this.scrollEl) {
      this.resizeObserver.observe(this.scrollEl);
      this.scrollEl.addEventListener('scroll', this.recalculate);
    } else {
      console.error('Cannot find scrollElement', { scrollElement });
    }
  };

  initialize = () => {
    const { disabled } = this.props;

    this.disabled = !!disabled;

    window.addEventListener('scroll', this.recalculate);
    window.addEventListener('resize', this.recalculate);
    window.addEventListener('pageshow', this.recalculate);
    window.addEventListener('load', this.recalculate);
  };

  componentDidUpdate({ scrollElement, bottomOffset, topOffset, disabled }: Readonly<AdvancedStickyProps>): void {
    if (scrollElement !== this.props.scrollElement) {
      this.updateScrollEl();
    }

    if (disabled !== this.props.disabled) {
      this.disabled = !!this.props.disabled;
      this.recalculate();
    }

    if (topOffset !== this.props.topOffset || bottomOffset !== this.props.bottomOffset) {
      this.recalculate();
    }
  }

  componentDidMount(): void {
    this.initialize();
  }

  componentWillUnmount(): void {
    if (this.scrollEl) {
      this.resizeObserver.unobserve(this.scrollEl);
      this.scrollEl.removeEventListener('scroll', this.recalculate);
    }
    window.removeEventListener('scroll', this.recalculate);
    window.removeEventListener('resize', this.recalculate);
    window.removeEventListener('pageshow', this.recalculate);
    window.removeEventListener('load', this.recalculate);

    this.scrollEl = null;

    if (this.el) {
      this.resizeObserver.unobserve(this.el);
    }

    if (this.wrapperEl) {
      this.resizeObserver.unobserve(this.wrapperEl);
    }
  }

  render() {
    const { children } = this.props;
    const { elementRef } = this;
    const { style, wrapperStyle } = this.state;
    return (
      <div ref={this.wrapperRef} style={wrapperStyle}>
        {children({ elementRef, style })}
      </div>
    );
  }
}
